import {
  AloftDate,
  RecommendedCompsSources,
  isDefined,
  parseToolkitListingID,
} from '@aloft/toolkit-shared';
import {
  Block,
  Flex,
  Icon,
  IconButton,
  ProductColor,
  Tooltip,
  Typography,
} from '@aloft/toolkit-sui';
import {
  SortingState,
  Table,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Dispatch, ReactNode, SetStateAction, useMemo, useRef } from 'react';

import {
  LocationType,
  formatProximityRequirementTooltip,
} from '../../../utils/locationTypeProximityRequirementMiles.ts';
import {
  formatSaleDateRequirementTooltip,
  isSaleDateOutOfBounds,
} from '../../../utils/locationTypeSaleDateRequirementMonths.ts';
import { useRecCompSelectionContext } from '../RecCompSelectionContext.tsx';
import { PairedTwoLineCell } from '../internal/PairedTwoLineCell.tsx';
import { AddressColumn } from '../internal/gridHelpers/AddressColumn.tsx';
import { makeSelectionColumn } from '../internal/gridHelpers/SelectionColumn.tsx';
import { SimilarityScoreBreakdownPopover } from '../internal/gridHelpers/SimilarityScoreBreakdownPopover.tsx';
import { SimilarityScorePill } from '../internal/gridHelpers/SimilarityScorePill.tsx';
import { StopColumnDrag } from '../internal/gridHelpers/StopColumnDrag.tsx';
import { UADRatingsSpinner } from '../internal/gridHelpers/UADRatingsSpinner.tsx';
import { WarningTooltipIcon } from '../internal/gridHelpers/WarningTooltipIcon.tsx';
import {
  getSortedRowModelWithPinnedSubject,
  getSortedRowModelWithPinnedSubjectAndGroups,
} from '../internal/gridHelpers/getSortedRowModelWithPinnedSubject.ts';
import { CompSelectionProperty, CompSelectionState } from '../types.ts';
import { PINNED_COMP_TABLE_TANSTACK_IDS } from '../utils/compTableColumnsToTableState.ts';
import { formatPropertyTypeAbbreviation } from '../utils/formatPropertyTypeAbbreviation.ts';
import { RECOMMENDED_COMPS_STATUS_GROUP_ORDER, getStatusGroup } from '../utils/getStatusGroup.ts';

const INITIAL_PAGE_INDEX = 0;
const PAGE_SIZE = 500;
const INITIAL_VISIBLE_COMPS = 20;
const ADDRESS_COLUMN_SIZE = 240;
const SIMILARITY_COLUMN_SIZE = 96;

const formatDateCell = (date?: { format: (fmt: string) => string }) =>
  date ? date.format('MM/yy') : '';

const booleanToYesNo = (value?: boolean) => {
  if (value === undefined) return '';
  return value ? 'Yes' : 'No';
};

const formatAddressCell = (address: CompSelectionProperty['address']) =>
  `${address?.AddressLineText}, ${address?.CityName}, ${address?.StateCode} ${address?.PostalCode}`;

const wrap = (content: ReactNode) => (
  <Flex alignItems="center" justifyContent="center" width="100%">
    {content}
  </Flex>
);

const UADRatingCell = ({
  rating,
  prefix,
  isLoading,
  hasFailed,
  isSkipped,
  failureTooltip,
  commentary,
}: {
  rating?: number;
  prefix: 'Q' | 'C';
  isLoading?: boolean;
  hasFailed?: boolean;
  isSkipped?: boolean;
  failureTooltip: string;
  commentary?: string;
}) => {
  if (isLoading) return wrap(<UADRatingsSpinner />);

  if (rating !== undefined) {
    const label = `${prefix}${rating}`;
    return wrap(
      commentary ? (
        <Tooltip label={commentary}>
          <Typography __dangerDoNotUseSx={{ cursor: 'help' }}>{label}</Typography>
        </Tooltip>
      ) : (
        label
      ),
    );
  }

  if (isSkipped) {
    return wrap(
      <Tooltip label="UAD ratings image analysis is only available for the first 20 recommended comps.">
        <Typography variant="body2" color="textSecondary" __dangerDoNotUseSx={{ cursor: 'help' }}>
          -
        </Typography>
      </Tooltip>,
    );
  }

  if (hasFailed) {
    return wrap(
      <Tooltip label={failureTooltip}>
        <Typography variant="body2" color="textSecondary" __dangerDoNotUseSx={{ cursor: 'help' }}>
          ?
        </Typography>
      </Tooltip>,
    );
  }

  return '';
};

const UADRatingHeader = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <Flex alignItems="center" gap={1}>
    <Tooltip label={tooltip}>
      <Block __dangerDoNotUseSx={{ cursor: 'help' }}>
        <Icon icon="sparkle" size="md" color="buttonDefault" />
      </Block>
    </Tooltip>
    {label}
  </Flex>
);

export const useRecommendedCompSelectionTable = (
  data: CompSelectionProperty[],
  subject: CompSelectionProperty,
  comps: CompSelectionProperty[],
  sorting: SortingState,
  setSorting: Dispatch<SetStateAction<SortingState>>,
  rowSelection: CompSelectionState,
  setRowSelection: Dispatch<SetStateAction<CompSelectionState>>,
  source: RecommendedCompsSources,
  orderID: string,
  isInspectifyOrder: boolean,
  hasArvOption: boolean,
  locationType?: LocationType,
  proximityRequirementMiles?: number,
  effectiveDate?: AloftDate,
  saleDateRequirementMonths?: number,
  compSelectionTableVariant: 'sales' | 'rental' = 'sales',
  imageAnalysisRatingsEnabled: boolean = false,
  onFilterOpen?: () => void,
  columnOrder?: string[],
  columnVisibility?: Record<string, boolean>,
  isFilterPanelOpen?: boolean,
  isExpanded: boolean = false,
): {
  table: Table<CompSelectionProperty>;
  rowsToPin: number;
  tableData: CompSelectionProperty[];
} => {
  const {
    openSimilarityScoreWeightingModal,
    openFilterDrawer,
    openQualityTierBreakdown,
    isFilterPanelOpen: isFilterPanelOpenFromContext,
  } = useRecCompSelectionContext();
  const resolvedIsFilterPanelOpen = isFilterPanelOpen ?? isFilterPanelOpenFromContext;
  const resolvedOnFilterOpen = onFilterOpen ?? openFilterDrawer;
  const tableData = useMemo(() => [subject, ...data], [subject, data]);

  const rowsToPin = 1;

  const rowSelectionRef = useRef<CompSelectionState>(rowSelection);
  rowSelectionRef.current = rowSelection;
  const onFilterOpenRef = useRef(resolvedOnFilterOpen);
  onFilterOpenRef.current = resolvedOnFilterOpen;
  const openSimilarityScoreWeightingModalRef = useRef(openSimilarityScoreWeightingModal);
  openSimilarityScoreWeightingModalRef.current = openSimilarityScoreWeightingModal;
  const openQualityTierBreakdownRef = useRef(openQualityTierBreakdown);
  openQualityTierBreakdownRef.current = openQualityTierBreakdown;

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<CompSelectionProperty>();

    const [qualityCol, conditionCol] = imageAnalysisRatingsEnabled
      ? [
          columnHelper.accessor((p) => p.uadQualityRating, {
            id: 'Qlty.',
            header: () => (
              <UADRatingHeader
                label="Qlty."
                tooltip="AI Image Analysis recommended UAD quality rating"
              />
            ),
            cell: ({ row: { original } }) => (
              <UADRatingCell
                rating={original.uadQualityRating}
                prefix="Q"
                isLoading={original.uadRatingsLoading}
                hasFailed={original.uadQualityRatingFailed}
                isSkipped={original.uadRatingsSkipped}
                failureTooltip="Failed to analyze the quality rating"
                commentary={original.uadQualityRatingCommentary}
              />
            ),
          }),
          columnHelper.accessor((p) => p.uadConditionRating, {
            id: 'Cond.',
            header: () => (
              <UADRatingHeader
                label="Cond."
                tooltip="AI Image Analysis recommended UAD condition rating"
              />
            ),
            cell: ({ row: { original } }) => (
              <UADRatingCell
                rating={original.uadConditionRating}
                prefix="C"
                isLoading={original.uadRatingsLoading}
                hasFailed={original.uadConditionRatingFailed}
                isSkipped={original.uadRatingsSkipped}
                failureTooltip="Failed to analyze the condition rating"
                commentary={original.uadConditionRatingCommentary}
              />
            ),
          }),
        ]
      : [undefined, undefined];

    const selectionCol = makeSelectionColumn(
      () => rowSelectionRef.current,
      setRowSelection,
      isInspectifyOrder,
      hasArvOption,
      () => onFilterOpenRef.current?.(),
      resolvedIsFilterPanelOpen,
    );

    const addressCol = columnHelper.accessor((p) => formatAddressCell(p.address), {
      id: 'Address',
      size: ADDRESS_COLUMN_SIZE,
      minSize: ADDRESS_COLUMN_SIZE,
      maxSize: ADDRESS_COLUMN_SIZE,
      cell: ({ row }) => AddressColumn(row.original),
    });

    const similarityScoreCol = columnHelper.accessor((p) => p.similarityScore, {
      id: 'Similarity Score',
      size: SIMILARITY_COLUMN_SIZE,
      minSize: SIMILARITY_COLUMN_SIZE,
      maxSize: SIMILARITY_COLUMN_SIZE,
      header: () => (
        <Flex alignItems="center" gap="4px">
          <StopColumnDrag>
            <IconButton
              icon="balance"
              size="xs"
              variant="secondary"
              ariaLabel="Similarity score weighting"
              onClick={(e) => {
                e.stopPropagation();
                openSimilarityScoreWeightingModalRef.current?.();
              }}
            />
          </StopColumnDrag>
          Similarity
        </Flex>
      ),
      cell: ({ row: { original } }) => {
        if (!isDefined(original.similarityScore) || original.id === 'subject') return '';

        const scorePercent = Math.round(original.similarityScore * 100);

        const scoreContent = (
          <Flex alignItems="center" gap="8px">
            <Typography variant="body2" color="textPrimary">
              {scorePercent}
            </Typography>
            <SimilarityScorePill score={scorePercent} />
          </Flex>
        );

        if (
          original.similarityBreakdown &&
          original.similarityCharacteristics &&
          subject.similarityCharacteristics
        ) {
          return (
            <SimilarityScoreBreakdownPopover
              breakdown={original.similarityBreakdown}
              subjectCharacteristics={subject.similarityCharacteristics}
              compCharacteristics={original.similarityCharacteristics}
              compSaleDate={original.saleDate}
            >
              {scoreContent}
            </SimilarityScoreBreakdownPopover>
          );
        }

        return scoreContent;
      },
    });

    const proximityCol = columnHelper.accessor((p) => p.distanceToSubject, {
      id: 'Proximity',
      cell: ({ row: { original } }) => {
        if (original.id === 'subject' || original.distanceToSubject === undefined) return '';

        const distanceText = `${original.distanceToSubject} miles`;
        const showWarning =
          locationType !== undefined &&
          proximityRequirementMiles !== undefined &&
          original.distanceToSubject > proximityRequirementMiles;

        return (
          <Flex alignItems="center" justifyContent="space-between" width="100%" gap="8px">
            <Typography variant="body2" color="textPrimary">
              {distanceText}
            </Typography>
            <Block width="18px" height="18px">
              {showWarning && (
                <WarningTooltipIcon
                  label={formatProximityRequirementTooltip(
                    locationType!,
                    proximityRequirementMiles!,
                  )}
                />
              )}
            </Block>
          </Flex>
        );
      },
    });

    const glaCol = columnHelper.accessor((p) => p.gla?.toSqFt().getUnitAmount(), {
      id: 'GLA',
      cell: ({ row: { original } }) => original.gla?.toString(),
    });

    const cdomCol = columnHelper.accessor((p) => p.cumulativeDaysOnMarket, {
      id: 'CDOM',
      cell: ({ row: { original } }) => original.cumulativeDaysOnMarket?.toString(),
    });

    const propertyTypeCol = columnHelper.accessor((p) => p.propertyType, {
      id: 'Property Type',
      header: 'Type',
      cell: ({ row }) => formatPropertyTypeAbbreviation(row.original.propertyType),
    });

    const yearBuiltCol = columnHelper.accessor((p) => p.yearBuilt, {
      id: 'Year Built',
      header: 'Year',
      cell: ({ row: { original } }) => original.yearBuilt?.toString(),
    });

    const lotSizeCol = columnHelper.accessor((p) => p.lotSize?.toSqFt().getUnitAmount(), {
      id: 'Lot Size',
      header: 'Site Size',
      cell: ({ row: { original } }) => original.lotSize?.toRoundedString(),
    });

    const bedsCol = columnHelper.accessor((p) => p.bedrooms, {
      id: 'Beds',
      cell: ({ row: { original } }) => original.bedrooms?.toString(),
    });

    const fullBathsCol = columnHelper.accessor((p) => p.fullBaths, {
      id: 'Full Baths',
      cell: ({ row: { original } }) => original.fullBaths?.toString(),
    });

    const halfBathsCol = columnHelper.accessor((p) => p.halfBaths, {
      id: 'Half Baths',
      cell: ({ row: { original } }) => original.halfBaths?.toString(),
    });

    const garageCol = columnHelper.accessor((p) => p.garageCount, {
      id: 'Garage Count',
      header: 'Garage',
      cell: ({ row: { original } }) => original.garageCount?.toString(),
    });

    const basementCol = columnHelper.accessor((p) => p.basement, {
      id: 'Basement',
      cell: ({ row: { original } }) => booleanToYesNo(original.basement),
    });

    const poolCol = columnHelper.accessor((p) => p.pool, {
      id: 'Pool',
      cell: ({ row: { original } }) => booleanToYesNo(original.pool ?? false),
    });

    const mlsIdCol = columnHelper.accessor((p) => p.listingID, {
      id: 'MLS ID',
      cell: ({ row: { original } }) => {
        if (!original.listingID) return '';
        const parsed = parseToolkitListingID(original.listingID);
        return parsed ? `#${parsed.listingID}` : original.listingID;
      },
    });

    // ── Rental-only columns ────────────────────────────────────────────────

    const rentRateCol = columnHelper.accessor(
      (p) => p.rentRate?.toDollarLike() ?? p.listPrice?.toDollarLike(),
      {
        id: 'Rent Rate',
        header: () => (
          <Flex flexDirection="column" gap="2px">
            <span>Rent Rate</span>
            <Typography variant="body2" color="textSecondary">
              List Price
            </Typography>
          </Flex>
        ),
        cell: ({ row: { original } }) => {
          const primary = original.rentRate ?? original.listPrice;
          return (
            <Flex flexDirection="column">
              <Typography variant="body2" color="textPrimary">
                {primary?.toString(true) ?? ''}
              </Typography>
              {original.listPrice && original.rentRate && (
                <Typography variant="body2" color="textSecondary">
                  {original.listPrice.toString(true)}
                </Typography>
              )}
            </Flex>
          );
        },
      },
    );

    const leaseDateCol = columnHelper.accessor((p) => p.leaseDate ?? p.saleDate, {
      id: 'Lease Date',
      cell: ({ row: { original } }) => formatDateCell(original.leaseDate ?? original.saleDate),
    });

    const rentPerSqftCol = columnHelper.accessor((p) => p.rentRate ?? p.listPrice, {
      id: 'Rent / sqft',
      cell: ({ row: { original } }) => {
        const rent = original.rentRate ?? original.listPrice;
        const glaSqft = original.gla?.toSqFt().getUnitAmount();
        return rent && glaSqft ? `${rent.div(glaSqft || 1).toString(true)}/sqft` : '';
      },
    });

    if (compSelectionTableVariant === 'rental') {
      return [
        selectionCol,
        addressCol,
        similarityScoreCol,
        proximityCol,
        rentRateCol,
        glaCol,
        leaseDateCol,
        cdomCol,
        rentPerSqftCol,
        propertyTypeCol,
        yearBuiltCol,
        lotSizeCol,
        bedsCol,
        fullBathsCol,
        halfBathsCol,
        garageCol,
        basementCol,
        poolCol,
        mlsIdCol,
      ].filter(isDefined);
    }

    const salePriceCol = columnHelper.accessor((p) => p.salePrice?.toDollarLike(), {
      id: 'Sale Price',
      header: () => <PairedTwoLineCell primary="Sale Price" secondary="List Price" />,
      cell: ({ row: { original } }) => (
        <PairedTwoLineCell
          primary={original.salePrice?.toString(true) ?? ''}
          secondary={original.listPrice?.toString(true) ?? ''}
        />
      ),
    });

    const qualityTierCol = columnHelper.accessor((p) => p.qualityTier, {
      id: 'Quality Tier',
      header: () => (
        <Flex alignItems="center" gap="4px">
          <StopColumnDrag>
            <IconButton
              icon="quality-tiers"
              size="xs"
              variant="secondary"
              ariaLabel="Quality tier"
              onClick={(e) => {
                e.stopPropagation();
                openQualityTierBreakdownRef.current?.();
              }}
            />
          </StopColumnDrag>
          Q Tier
        </Flex>
      ),
      cell: ({ row: { original } }) => {
        if (original.qualityTier === undefined) return '';
        return (
          <Flex alignItems="center" gap="8px">
            <Block
              width="16px"
              height="16px"
              backgroundColor={`qualityTier${original.qualityTier}` as ProductColor}
            />
            Tier {original.qualityTier}
          </Flex>
        );
      },
    });

    const saleDateCol = columnHelper.accessor((p) => p.saleDate, {
      id: 'Sale Date',
      header: () => <PairedTwoLineCell primary="Sale Date" secondary="Contract Date" />,
      cell: ({ row: { original } }) => {
        if (original.id === 'subject') {
          return (
            <PairedTwoLineCell
              primary={formatDateCell(original.saleDate)}
              secondary={formatDateCell(original.contractDate)}
            />
          );
        }

        const showWarning =
          locationType !== undefined &&
          isSaleDateOutOfBounds(original.saleDate, effectiveDate, saleDateRequirementMonths);

        return (
          <Flex alignItems="center" gap="4px">
            <PairedTwoLineCell
              primary={formatDateCell(original.saleDate)}
              secondary={formatDateCell(original.contractDate)}
            />
            {showWarning && saleDateRequirementMonths !== undefined && (
              <WarningTooltipIcon
                label={formatSaleDateRequirementTooltip(locationType!, saleDateRequirementMonths)}
              />
            )}
          </Flex>
        );
      },
    });

    return [
      selectionCol,
      addressCol,
      similarityScoreCol,
      proximityCol,
      salePriceCol,
      qualityTierCol,
      glaCol,
      qualityCol,
      conditionCol,
      propertyTypeCol,
      yearBuiltCol,
      lotSizeCol,
      saleDateCol,
      cdomCol,
      bedsCol,
      fullBathsCol,
      halfBathsCol,
      garageCol,
      basementCol,
      poolCol,
      mlsIdCol,
    ].filter(isDefined);
  }, [
    compSelectionTableVariant,
    effectiveDate,
    hasArvOption,
    imageAnalysisRatingsEnabled,
    isInspectifyOrder,
    locationType,
    proximityRequirementMiles,
    resolvedIsFilterPanelOpen,
    saleDateRequirementMonths,
    setRowSelection,
    subject,
  ]);

  const getSortedRowModelFn = useMemo(
    () =>
      compSelectionTableVariant === 'sales'
        ? getSortedRowModelWithPinnedSubjectAndGroups(
            rowsToPin,
            (row: CompSelectionProperty) => getStatusGroup(row.mlsStatus),
            RECOMMENDED_COMPS_STATUS_GROUP_ORDER,
          )
        : getSortedRowModelWithPinnedSubject(rowsToPin),
    [compSelectionTableVariant, rowsToPin],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    enableSorting: true,
    enablePinning: true,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModelFn,
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting,
      pagination: {
        pageIndex: INITIAL_PAGE_INDEX,
        pageSize: isExpanded ? PAGE_SIZE : INITIAL_VISIBLE_COMPS,
      },
      columnOrder,
      columnVisibility,
      columnPinning: { left: [...PINNED_COMP_TABLE_TANSTACK_IDS] },
    },
  });

  return { table, rowsToPin, tableData };
};
